'use client';

import { Button } from '@heroui/react';
import {
  FaPhone,
  FaArrowRotateLeft,
  FaPhoneVolume,
  FaMapLocationDot,
  FaBuilding,
} from 'react-icons/fa6';
import { Company } from '@/util/types/serverTypes';
import { useScheduler } from '../SchedulerContext';
import { EscapeHatchReason } from '../utils/checkEscapeHatch';
import { ReactNode } from 'react';
import { parsePhoneNumber } from 'libphonenumber-js';

interface EscapeHatchPageProps {
  reason: Exclude<EscapeHatchReason, null>;
  company: Company;
  onStartOver: () => void;
}

const REASON_CONFIG: Record<
  Exclude<EscapeHatchReason, null>,
  {
    title: string;
    message: string;
    icon: ReactNode;
  }
> = {
  'service-blocked': {
    title: 'Phone Consultation Required',
    message:
      'The services you selected require a phone consultation to book. Our team will be happy to assist you in scheduling your appointment.',
    icon: <FaPhoneVolume className='text-6xl text-blue-500' />,
  },
  'out-of-area': {
    title: 'Outside Service Area',
    message:
      "We're sorry, but the property address is outside our current service area. Our team will reach out to determine if we can service this property.",
    icon: <FaMapLocationDot className='text-6xl text-red-500' />,
  },
  'size-limit': {
    title: 'Special Property Size Consultation',
    message:
      'Properties of this size require a phone consultation for accurate scheduling and pricing. Our team will work with you to arrange the perfect inspection.',
    icon: <FaBuilding className='text-6xl text-orange-500' />,
  },
};

export default function EscapeHatchPage({
  reason,
  company,
  onStartOver,
}: EscapeHatchPageProps) {
  const { companyStyles } = useScheduler();
  const config = REASON_CONFIG[reason];

  // Format phone number for display
  const formatPhoneNumber = (phone: string) => {
    try {
      const phoneNumber = parsePhoneNumber(phone);
      return phoneNumber.formatNational(); // Returns format like (123) 456-7890
    } catch {
      return phone; // Fallback to original if parsing fails
    }
  };

  return (
    <div className='mx-auto flex min-h-[600px] w-full max-w-2xl flex-col items-center justify-center px-4 py-12'>
      {/* Icon */}
      <div className='mb-6'>{config.icon}</div>

      {/* Title */}
      <h1 className='mb-4 text-center text-3xl font-bold text-gray-900'>
        {config.title}
      </h1>

      {/* Message */}
      <p className='mb-8 text-center text-lg text-gray-600'>{config.message}</p>

      {/* Contact Information Card */}
      <div className='mb-8 w-full rounded-lg border border-gray-200 bg-gray-50 p-6 text-center'>
        <h2 className=' text-lg font-semibold text-gray-800'>
          We will reach out to you shortly.
        </h2>
        <p className='mb-4 text-xs text-gray-600'>
          Feel free to contact us as well.
        </p>

        <div className='space-y-2 text-gray-700'>
          {company.name && <p className='font-medium'>{company.name}</p>}
          {company.phone && (
            <p>
              <span className='font-medium'>Phone:</span>{' '}
              <a
                href={`tel:${company.phone.replace(/^\+/, '')}`}
                className='hover:underline'
                style={{ color: companyStyles.companyColor || '#2563eb' }}
              >
                {formatPhoneNumber(company.phone)}
              </a>
            </p>
          )}
          {company.email && (
            <p>
              <span className='font-medium'>Email:</span>{' '}
              <a
                href={`mailto:${company.email}`}
                className='hover:underline'
                style={{ color: companyStyles.companyColor || '#2563eb' }}
              >
                {company.email}
              </a>
            </p>
          )}
        </div>
      </div>

      {/* Call to Action */}
      <div className='mb-4 flex flex-col items-center gap-4 sm:flex-row'>
        {company.phone && (
          <Button
            as='a'
            href={`tel:${company.phone.replace(/^\+/, '')}`}
            size='lg'
            className={companyStyles.primaryButtonClassName}
            style={companyStyles.primaryButtonStyles}
            startContent={<FaPhone />}
          >
            Call to Schedule
          </Button>
        )}

        <Button
          size='lg'
          variant='bordered'
          onPress={onStartOver}
          startContent={<FaArrowRotateLeft />}
        >
          Start Over
        </Button>
      </div>

      {/* Additional help text */}
      <p className='mt-6 text-center text-sm text-gray-500'>
        Our team is ready to help you schedule your appointment
      </p>
    </div>
  );
}
